import { dateWithoutTimezone } from '@/infrastructure/helpers/date-manipulation';
import { computed, defineComponent, onMounted, onUnmounted, PropType, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { toTypedSchema } from '@vee-validate/yup';
import { validationFields, ValidationFieldsType } from '../dl-form-field/dl-form-field-validation-utils';
import { DlDayJs } from '@/infrastructure/dayjs/dayjs';

export interface IDlPeriodDate {
  start: Date[];
  end: Date[];
}

const createDateSchema = () => {
  return yup.object({
    date: yup
      .object({
        start: yup
          .array()
          .of(yup.date().required())
          .min(1, 'Startdatum is verplicht')
          .max(1, 'Er mag maar één startdatum geselecteerd worden')
          .label('Startdatum'),
        end: yup
          .array()
          .of(yup.date().required())
          .min(1, 'Einddatum is verplicht')
          .max(1, 'Er mag maar één einddatum geselecteerd worden')
          .test('end-after-start', 'Einddatum moet na startdatum liggen', function (value) {
            const start = this.parent.start;
            if (!start || start.length === 0 || !value || value.length === 0) return true;
            return value[0] > start[0];
          })
          .label('Einddatum')
      })
      .default({ start: [], end: [] }) // ensure object exists
  });
};

export default defineComponent({
  name: 'dl-period',
  props: {
    minDate: {
      type: Date as PropType<Date>,
      default: null
    },
    maxDate: {
      type: Date as PropType<Date>,
      default: null
    },
    startMinDate: {
      type: Date as PropType<Date>,
      default: null
    },
    startMaxDate: {
      type: Date as PropType<Date>,
      default: null
    },
    endMinDate: {
      type: Date as PropType<Date>,
      default: null
    },
    endMaxDate: {
      type: Date as PropType<Date>,
      default: null
    },
    defaultMinDate: {
      type: Date as PropType<Date>,
      default: null
    },
    defaultMaxDate: {
      type: Date as PropType<Date>,
      default: null
    },
    modRequired: {
      type: Boolean,
      default: false
    }
  },
  emits: ['date-change', 'has-error'],
  setup(_props, { emit }) {
    const schema = createDateSchema();
    const form = useForm({
      validationSchema: toTypedSchema(schema),
      initialValues: { date: { start: [], end: [] } } // ensure arrays exist from the start
    });
    const dateSchema = schema.fields.date as yup.ObjectSchema<IDlPeriodDate>;
    const fields: ValidationFieldsType<typeof dateSchema> = validationFields<typeof dateSchema>(dateSchema, 'date');

    const start = fields.start.getValue<Date[]>();
    const end = fields.end.getValue<Date[]>();

    const startDate = ref();
    const endDate = ref();

    const minStartDate = computed(() => {
      if (_props.startMinDate)
        return DlDayJs(dateWithoutTimezone(_props.startMinDate)).startOf('day').toDate().toISOString();
      if (_props.minDate) return DlDayJs(dateWithoutTimezone(_props.minDate)).startOf('day').toDate().toISOString();
      return null;
    });
    const maxStartDate = computed(() => {
      if (_props.startMaxDate)
        return DlDayJs(dateWithoutTimezone(_props.startMaxDate)).startOf('day').toDate().toISOString();
      if (end.value?.length) return DlDayJs(dateWithoutTimezone(end.value[0])).startOf('day').toDate().toISOString();
      return null;
    });

    const minEndDate = computed(() => {
      let fromStart: Date | null = null;
      if (start.value?.length && start.value[0]) {
        fromStart = DlDayJs(start.value[0]).add(1, 'day').startOf('day').toDate();
      }

      const fromProps =
        (_props.endMinDate as Date | null) ??
        (_props.minDate ? (DlDayJs(dateWithoutTimezone(_props.minDate)).startOf('day').toDate() as Date) : null);

      const effective =
        fromStart && fromProps ? (fromStart > fromProps ? fromStart : fromProps) : (fromStart ?? fromProps);

      return effective ? effective.toISOString() : null;
    });

    const maxEndDate = computed(() => {
      if (_props.endMaxDate)
        return DlDayJs(dateWithoutTimezone(_props.endMaxDate)).startOf('day').toDate().toISOString();
      if (_props.maxDate) return DlDayJs(dateWithoutTimezone(_props.maxDate)).startOf('day').toDate().toISOString();
      return null;
    });

    const onStartChange = (e: Event) => {
      const inputEl = e.target as HTMLInputElement;
      const inputValue = inputEl.value.trim();
      if (inputEl && !inputValue) {
        start.value = [];
        return;
      }

      const picked = DlDayJs(inputValue, 'D/M/YYYY').startOf('day');
      if (!picked.isValid()) {
        fields.start.field.setErrors('Ongeldige datum');
        return;
      }

      start.value = [picked.toDate()];

      if (minStartDate.value && picked.isBefore(DlDayJs(minStartDate.value), 'day')) {
        fields.start.field.setErrors(
          `Startdatum mag niet voor ${DlDayJs(minStartDate.value).toDate().toLocaleDateString('nl-be')} liggen`
        );
        return;
      }

      if (maxStartDate.value && picked.isAfter(DlDayJs(maxStartDate.value), 'day')) {
        fields.start.field.setErrors(
          `Startdatum mag niet na ${DlDayJs(maxStartDate.value).toDate().toLocaleDateString('nl-be')} liggen`
        );
        return;
      }

      fields.start.field.setErrors('');
    };

    const onEndChange = (e: Event) => {
      const inputEl = e.target as HTMLInputElement;
      const inputValue = inputEl.value.trim();
      if (inputEl && !inputValue) {
        end.value = [];
        return;
      }

      const picked = DlDayJs(inputValue, 'D/M/YYYY').startOf('day');
      if (!picked.isValid()) {
        fields.end.field.setErrors('Ongeldige datum');
        return;
      }

      end.value = [picked.toDate()];

      if (minEndDate.value && picked.isBefore(DlDayJs(minEndDate.value), 'day')) {
        fields.end.field.setErrors(
          `Einddatum mag niet voor ${DlDayJs(minEndDate.value).toDate().toLocaleDateString('nl-be')} liggen`
        );
        return;
      }

      if (maxEndDate.value && picked.isAfter(DlDayJs(maxEndDate.value), 'day')) {
        fields.end.field.setErrors(
          `Einddatum mag niet na ${DlDayJs(maxEndDate.value).toDate().toLocaleDateString('nl-be')} liggen`
        );
        return;
      }

      fields.end.field.setErrors('');
    };

    onMounted(() => {
      const defaultEndDate = DlDayJs(dateWithoutTimezone(_props.defaultMaxDate)).startOf('day').toDate();
      const maxAllowedEndDate = _props.endMaxDate
        ? DlDayJs(dateWithoutTimezone(_props.endMaxDate)).startOf('day').toDate()
        : null;
      end.value = [maxAllowedEndDate && defaultEndDate > maxAllowedEndDate ? maxAllowedEndDate : defaultEndDate];

      const defaultStartDate = DlDayJs(dateWithoutTimezone(_props.defaultMinDate)).startOf('day').toDate();
      const maxAllowedStartDate = maxStartDate.value ? DlDayJs(maxStartDate.value).startOf('day').toDate() : null;
      start.value = [
        maxAllowedStartDate && defaultStartDate > maxAllowedStartDate ? maxAllowedStartDate : defaultStartDate
      ];

      const startDateInputEl = startDate.value?.$el?.querySelector(
        'input:not([type="hidden"])'
      ) as HTMLInputElement | null;
      if (startDateInputEl) {
        startDateInputEl.addEventListener('focusout', onStartChange);
        startDateInputEl.addEventListener('change', onStartChange);
      }

      const endDateInputEl = endDate.value?.$el?.querySelector('input:not([type="hidden"])') as HTMLInputElement | null;
      if (endDateInputEl) {
        endDateInputEl.addEventListener('focusout', onEndChange);
        endDateInputEl.addEventListener('change', onEndChange);
      }
    });

    onUnmounted(() => {
      const startDateInputEl = startDate.value?.$el?.querySelector(
        'input:not([type="hidden"])'
      ) as HTMLInputElement | null;
      if (startDateInputEl) {
        startDateInputEl.removeEventListener('focusout', onStartChange);
        startDateInputEl.removeEventListener('change', onStartChange);
      }

      const endDateInputEl = endDate.value?.$el?.querySelector('input:not([type="hidden"])') as HTMLInputElement | null;
      if (endDateInputEl) {
        endDateInputEl.removeEventListener('focusout', onEndChange);
        endDateInputEl.removeEventListener('change', onEndChange);
      }
    });

    watch(
      [start, end],
      () => {
        const payload: IDlPeriodDate = {
          start: start.value ?? [],
          end: end.value ?? []
        };
        emit('date-change', payload);
      },
      { deep: true }
    );

    watch(form.errors, (errors) => {
      const hasError = Object.keys(errors).length > 0;
      emit('has-error', hasError);
    });

    return {
      start,
      end,
      startDate,
      endDate,
      maxStartDate,
      minEndDate,
      minStartDate,
      maxEndDate,
      fields,
      form
    };
  }
});
